<template>
  <Teleport to="body">
<div class="modal fade" tabindex="-1" :id="modalId">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Compartilhar prontuário</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-6" v-if="medicalRecord">
            {{ date(medicalRecord.medrec_date_time).format('DD/MM/YYYY') }}
            — {{ medicalRecord.record_type?.rectype_name }}
          </p>

          <form class="row g-4 align-items-end mb-8" @submit.prevent="storeShare">
            <div class="col-md-5">
              <label class="form-label required">Profissional do local</label>
              <select class="form-select form-select-solid" v-model="form.id_prof_viewer" required>
                <option value="" disabled>Selecione</option>
                <option v-for="professional in viewerProfessionals" :key="professional.id_prof" :value="professional.id_prof">
                  {{ professional.prof_name }}
                </option>
              </select>
            </div>
            <div class="col-md-5">
              <label class="form-label required">Tipo</label>
              <select class="form-select form-select-solid" v-model="form.share_scope" required>
                <option value="single">Apenas este</option>
                <option value="from_now">Este e os próximos</option>
                <option value="all">Todos deste paciente</option>
              </select>
            </div>
            <div class="col-md-2">
              <button type="submit" class="btn btn-primary w-100" :disabled="saving">
                <i class="bi bi-share"></i>
              </button>
            </div>
          </form>

          <h6 class="fw-bold mb-4">Acompanhamentos ativos</h6>
          <table class="table table-row-bordered gy-4 mb-8">
            <thead>
            <tr class="fw-bold fs-7 text-muted">
              <th>Profissional</th>
              <th>Tipo</th>
              <th class="text-end">Ações</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="patientShare in patientShares" :key="patientShare.id_medrec_patient_share">
              <td>{{ patientShare.viewer?.prof_name }}</td>
              <td>{{ patientShareScopeLabel(patientShare.share_scope) }}</td>
              <td class="text-end">
                <button class="btn btn-sm btn-light-danger" @click="stopPatientShare(patientShare)">
                  Parar
                </button>
              </td>
            </tr>
            <tr v-if="patientShares.length === 0">
              <td colspan="3" class="text-muted text-center py-6">Nenhum acompanhamento contínuo.</td>
            </tr>
            </tbody>
          </table>

          <h6 class="fw-bold mb-4">Compartilhamentos deste registro</h6>
          <table class="table table-row-bordered gy-4">
            <thead>
            <tr class="fw-bold fs-7 text-muted">
              <th>Profissional</th>
              <th class="text-end">Ações</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="share in shares" :key="share.id_medrec_share">
              <td>{{ share.viewer?.prof_name }}</td>
              <td class="text-end">
                <button class="btn btn-sm btn-light-danger" @click="revokeShare(share)">
                  <i class="bi bi-trash"></i> Revogar
                </button>
              </td>
            </tr>
            <tr v-if="shares.length === 0">
              <td colspan="2" class="text-muted text-center py-6">Nenhum compartilhamento ativo.</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Modal from "@/helpers/modal";

export default {
  name: "ModalShareMedicalRecord",
  props: {
    medicalRecord: Object,
    modalId: {
      type: String,
      default: 'modal_share_medrec',
    },
  },
  data() {
    return {
      professionals: [],
      shares: [],
      patientShares: [],
      saving: false,
      form: {
        id_prof_viewer: '',
        share_scope: 'from_now',
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    viewerProfessionals() {
      const ownerId = this.medicalRecord?.id_prof;
      const sharedIds = [
        ...this.shares.map((s) => Number(s.id_prof_viewer)),
        ...this.patientShares.map((s) => Number(s.id_prof_viewer)),
      ];
      return this.professionals.filter((p) => {
        const id = Number(p.id_prof);
        return id !== Number(ownerId) && !sharedIds.includes(id);
      });
    },
  },
  watch: {
    medicalRecord(value) {
      if (value?.id_medrec) {
        this.load();
      }
    },
  },
  methods: {
    date: moment,
    open() {
      this.load().then(() => {
        Modal.toggle(`#${this.modalId}`);
      });
    },
    load() {
      if (!this.medicalRecord?.id_medrec) {
        return Promise.resolve();
      }
      return Promise.all([this.loadProfessionals(), this.loadShares()]);
    },
    loadProfessionals() {
      return http.get(`/places/${this.place.id_place}/professionals`).then((response) => {
        this.professionals = response.data || [];
      });
    },
    loadShares() {
      return http.get(`/places/${this.place.id_place}/medical-records/${this.medicalRecord.id_medrec}/shares`)
        .then((response) => {
          this.shares = response.data?.shares || [];
          this.patientShares = response.data?.patient_shares || [];
        });
    },
    patientShareScopeLabel(scope) {
      if (scope === 'all') {
        return 'Todos';
      }
      if (scope === 'from_now') {
        return 'Este e os próximos';
      }
      return 'Apenas este';
    },
    storeShare() {
      this.saving = true;
      http.post(`/places/${this.place.id_place}/medical-records/${this.medicalRecord.id_medrec}/shares`, this.form)
        .then(() => {
          Alert.toastSuccess('Prontuário compartilhado.');
          this.form.id_prof_viewer = '';
          this.form.share_scope = 'from_now';
          this.loadShares();
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao compartilhar.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    revokeShare(share) {
      Swal.fire({
        title: 'Revogar compartilhamento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, revogar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        http.delete(`/places/${this.place.id_place}/medical-records/${this.medicalRecord.id_medrec}/shares/${share.id_medrec_share}`)
          .then(() => {
            Alert.toastSuccess('Compartilhamento revogado.');
            this.loadShares();
          })
          .catch((e) => {
            Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao revogar.');
          });
      });
    },
    stopPatientShare(patientShare) {
      Swal.fire({
        title: 'Parar acompanhamento?',
        text: 'O profissional deixará de receber este e os próximos prontuários deste paciente.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, parar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        http.delete(`/places/${this.place.id_place}/medical-record-patient-shares/${patientShare.id_medrec_patient_share}`)
          .then(() => {
            Alert.toastSuccess('Acompanhamento encerrado.');
            this.loadShares();
          })
          .catch((e) => {
            Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao parar.');
          });
      });
    },
  },
};
</script>
